import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Images, LogOut, Store, Users } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import { api, resolveMediaUrl } from "@/lib/api";
import SalesQueue from "./SalesQueue";

export default function SalesDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [retailer, setRetailer] = useState(null);
  const [renders, setRenders] = useState([]);

  useEffect(() => {
    api.get("/sales/retailer").then((r) => setRetailer(r.data)).catch(() => {});
    api.get("/sales/history").then((r) => setRenders(r.data)).catch(() => {});
  }, []);

  const onLogout = () => {
    logout();
    navigate("/sales/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E5E5] sticky top-0 z-30">
        <div className="px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Logo />
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={() => navigate("/sales/visualizer")}
              className="neivai-btn-primary inline-flex items-center gap-1.5 whitespace-nowrap px-3 text-xs sm:gap-2 sm:px-4 sm:text-sm"
            >
              <Sparkles className="h-4 w-4" />
              <span className="sm:hidden">Visualize</span>
              <span className="hidden sm:inline">Open Visualizer</span>
            </button>
            <button onClick={onLogout} className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-[#5C5F62] hover:text-[#B74F4F] transition sm:gap-2 sm:text-sm">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 px-4 sm:px-6 md:px-12 py-6 sm:py-10 space-y-8 sm:space-y-10 max-w-5xl mx-auto w-full">
        {/* Welcome */}
        <div>
          <span className="overline">Sales Executive</span>
          <h1 className="mt-3 text-4xl md:text-5xl font-display font-semibold tracking-tight">
            Hi, {user?.name || user?.email}
          </h1>
          {retailer && (
            <p className="mt-2 text-[#5C5F62] inline-flex items-center gap-1.5">
              <Store className="h-4 w-4" /> {retailer.business_name}
            </p>
          )}
        </div>

        {/* CTA card */}
        <div
          onClick={() => navigate("/sales/visualizer")}
          className="neivai-card p-6 sm:p-8 cursor-pointer hover:shadow-md transition flex flex-col items-start gap-4 bg-[#1A1C1E] text-white sm:flex-row sm:items-center sm:justify-between sm:gap-6"
        >
          <div>
            <h2 className="text-xl sm:text-2xl font-display font-medium">Start Visualizing</h2>
            <p className="mt-1 text-white/60 text-sm">Upload a room photo and place products for your customer.</p>
          </div>
          <Sparkles className="h-8 w-8 sm:h-10 sm:w-10 text-[#C86B53] shrink-0" />
        </div>

        {/* Customer Queue */}
        <div>
          <div className="flex items-center gap-2 mb-6">
            <Users className="h-5 w-5 text-[#C86B53]" />
            <h2 className="text-2xl font-display font-medium">Customer Queue</h2>
          </div>
          <SalesQueue />
        </div>

        {/* Recent renders */}
        <div>
          <div className="flex items-center gap-2 mb-6">
            <Images className="h-5 w-5 text-[#C86B53]" />
            <h2 className="text-2xl font-display font-medium">Recent Renders</h2>
          </div>
          {renders.length === 0 ? (
            <div className="neivai-card p-12 text-center text-[#8A8D91]">
              No renders yet. Open the visualizer to create your first one.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {renders.slice(0, 8).map((r) => (
                <div key={r.id} className="neivai-card overflow-hidden">
                  <div className="aspect-square bg-[#F3F1EC] overflow-hidden">
                    {r.output_urls?.[0] ? (
                      <img src={resolveMediaUrl(r.output_urls[0])} alt="render" className="h-full w-full object-cover" />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-xs text-[#8A8D91]">{r.status}</div>
                    )}
                  </div>
                  <p className="text-xs text-[#8A8D91] p-3">{new Date(r.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
